import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Globe from "react-globe.gl";
import * as THREE from "three";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Users, Sparkles, Landmark, Music, ShieldAlert, Clock, Swords, Coins, Ban, Globe2, Flag, Lightbulb, GraduationCap, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionLabel } from "../components/Primitives";

// Per-layer palettes
const PALETTES = {
  diaspora: {
    land: "#6d28d9", active: "#a855f7", hover: "#e9d5ff", selected: "#c084fc",
    side: "rgba(76,29,149,0.55)", stroke: "#7c3aed", globe: "#2e1065", atmosphere: "#a855f7",
    accent: "#c084fc", panelBg: "#150826", border: "#5b21b6", subText: "#cbb7e8", label: "#d8b4fe",
    bg: "radial-gradient(ellipse at 50% 30%, #2e1065 0%, #1a0b2e 45%, #0A0908 100%)",
  },
  colonizer: {
    land: "#7f1d1d", active: "#dc2626", hover: "#fca5a5", selected: "#f59e0b",
    side: "rgba(127,29,29,0.55)", stroke: "#b91c1c", globe: "#3b0a0a", atmosphere: "#f59e0b",
    accent: "#f59e0b", panelBg: "#1a0808", border: "#7f1d1d", subText: "#e7c9b8", label: "#fca5a5",
    bg: "radial-gradient(ellipse at 50% 30%, #3b0a0a 0%, #200606 45%, #0A0908 100%)",
  },
  resistance: {
    land: "#14532d", active: "#16a34a", hover: "#bbf7d0", selected: "#fbbf24",
    side: "rgba(20,83,45,0.55)", stroke: "#15803d", globe: "#052e16", atmosphere: "#fbbf24",
    accent: "#fbbf24", panelBg: "#08160c", border: "#166534", subText: "#c8e6cf", label: "#86efac",
    bg: "radial-gradient(ellipse at 50% 30%, #052e16 0%, #06210f 45%, #0A0908 100%)",
  },
};

const MODES = [
  { key: "diaspora", label: "Diaspora", icon: Globe2 },
  { key: "colonizer", label: "Colonizers & Slave Trade", icon: Coins },
  { key: "resistance", label: "Resistance", icon: Swords },
];

const INTRO = {
  diaspora: "Spin the globe and click any country to uncover when Africans arrived, the tribes they came from, and the extraordinary contributions of their descendants.",
  colonizer: "Explore the European and Middle Eastern nations that enslaved and colonized Africa. Click a nation to see the African lands it exploited — arcs trace each connection.",
  resistance: "Honor the African nations that fought back. Click a country to read the great battles and uprisings waged against colonial powers and slave-raiders.",
};

// Legend labels per mode (what the highlight colors mean).
const LEGEND = {
  diaspora: { active: "Curated diaspora history", selected: "Selected country", land: "Click to research on demand" },
  colonizer: { active: "Colonizer / slave-trade beneficiary", selected: "Selected nation", land: "Click to research on demand" },
  resistance: { active: "Nation with curated resistance history", selected: "Selected nation", land: "Click to research on demand" },
};

// Rotating "Did you know?" facts per mode.
const FACTS = {
  diaspora: [
    "Brazil received more enslaved Africans than any other country — an estimated 4.9 million, ten times the number brought to the United States.",
    "The banjo, born in the Americas, descends from West African stringed instruments like the akonting.",
    "Haiti's 1804 revolution was the only slave revolt in history to found an independent nation.",
    "Gullah/Geechee communities of the U.S. Sea Islands preserve rice-farming knowledge brought directly from Senegambia.",
    "An estimated 12.5 million Africans were forced across the Atlantic between the 16th and 19th centuries.",
  ],
  colonizer: [
    "Britain paid £20 million in 1833 to compensate slave OWNERS — a debt only fully repaid by taxpayers in 2015.",
    "King Leopold II ran the Congo as private property; an estimated 10 million Congolese died under his rubber regime.",
    "Zanzibar, ruled by Oman, hosted one of the largest slave markets on the Indian Ocean.",
    "France extorted a 150-million-franc 'independence indemnity' from Haiti that crippled it for over a century.",
    "The Zanj Rebellion (869–883 CE) saw enslaved East Africans in Iraq shake the mighty Abbasid Caliphate for 15 years.",
  ],
  resistance: [
    "At Adwa in 1896, Ethiopia became the first African nation to decisively defeat a European colonial army.",
    "At Isandlwana in 1879, a Zulu army armed mainly with spears annihilated a modern British force.",
    "Yaa Asantewaa led the Ashanti in the 1900 War of the Golden Stool — a woman commanding the last great uprising.",
    "Queen Njinga of Angola waged war against Portuguese slavers into her sixties.",
    "The Aba Women's War of 1929 saw tens of thousands of Nigerian women force the colonial government to back down.",
  ],
};


function nameOf(feat) {
  return feat?.properties?.ADMIN || feat?.properties?.NAME || feat?.properties?.NAME_LONG || "";
}
function codeOf(feat) {
  return feat?.properties?.ISO_A3 || feat?.properties?.ISO_A3_EH || "";
}

// Rough centroid of a GeoJSON feature (average of ring vertices of the largest polygon).
function centroidOf(feat) {
  const g = feat?.geometry;
  if (!g) return null;
  let rings = [];
  if (g.type === "Polygon") rings = g.coordinates;
  else if (g.type === "MultiPolygon") {
    // pick the polygon with the most points
    let best = null, bestLen = -1;
    for (const poly of g.coordinates) {
      const len = poly[0]?.length || 0;
      if (len > bestLen) { bestLen = len; best = poly; }
    }
    rings = best || [];
  }
  const outer = rings[0];
  if (!outer || !outer.length) return null;
  let x = 0, y = 0;
  for (const [lng, lat] of outer) { x += lng; y += lat; }
  return { lat: y / outer.length, lng: x / outer.length };
}

export default function GlobePage() {
  const { user, login } = useAuth();
  const globeRef = useRef();
  const wrapRef = useRef();
  const pollRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [dataCodes, setDataCodes] = useState(new Set());
  const [hoverD, setHoverD] = useState(null);
  const [dims, setDims] = useState({ w: 800, h: 600 });

  const [mode, setMode] = useState("diaspora");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slowResearch, setSlowResearch] = useState(false);
  const [factIdx, setFactIdx] = useState(0);
  const [learners, setLearners] = useState([]);

  const P = PALETTES[mode];

  // centroid lookup by ISO_A3
  const centroids = useMemo(() => {
    const m = {};
    for (const f of countries) {
      const c = codeOf(f);
      if (c) m[c] = centroidOf(f);
    }
    return m;
  }, [countries]);

  useEffect(() => {
    fetch("/countries.geojson").then((r) => r.json()).then((geo) => {
      setCountries(geo.features.filter((f) => nameOf(f) !== "Antarctica"));
    });
  }, []);

  // Load highlighted country codes whenever the mode changes.
  useEffect(() => {
    setSelected(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    api.get(`/diaspora?layer=${mode}`).then((r) => {
      setDataCodes(new Set(r.data.map((d) => d.country_code).filter(Boolean)));
    }).catch(() => setDataCodes(new Set()));
    setFactIdx(0);
  }, [mode]);

  // Rotate the "Did you know?" ticker.
  useEffect(() => {
    const facts = FACTS[mode] || [];
    if (facts.length < 2) return;
    const t = setInterval(() => setFactIdx((i) => (i + 1) % facts.length), 8000);
    return () => clearInterval(t);
  }, [mode]);

  // Load the educator's learners (for Globe exploration assignments).
  useEffect(() => {
    if (!user) { setLearners([]); return; }
    api.get("/learners").then((r) => setLearners(r.data)).catch(() => setLearners([]));
  }, [user]);

  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) {
        setDims({ w: wrapRef.current.clientWidth, h: Math.min(640, Math.max(420, window.innerHeight - 300)) });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    try {
      g.globeMaterial().color = new THREE.Color(P.globe);
      g.controls().autoRotate = true;
      g.controls().autoRotateSpeed = 0.45;
      g.controls().enableZoom = true;
      g.pointOfView({ lat: 8, lng: -30, altitude: 2.4 }, 0);
    } catch (e) {}
  }, [countries, P.globe]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const capColor = useCallback((feat) => {
    if (selected && codeOf(feat) === codeOf(selected)) return P.selected;
    if (feat === hoverD) return P.hover;
    if (dataCodes.has(codeOf(feat))) return P.active;
    return P.land;
  }, [hoverD, dataCodes, selected, P]);

  // Arcs: colonizer -> each African nation it colonized.
  const arcs = useMemo(() => {
    if (mode !== "colonizer") return [];
    const d = selected?.data;
    if (!d || !Array.isArray(d.colonized)) return [];
    const from = centroids[codeOf(selected)];
    if (!from) return [];
    const out = [];
    for (const c of d.colonized) {
      const to = centroids[c.country_code];
      if (to) out.push({ startLat: from.lat, startLng: from.lng, endLat: to.lat, endLng: to.lng });
    }
    return out;
  }, [mode, selected, centroids]);

  const stopRotate = () => { try { globeRef.current.controls().autoRotate = false; } catch (e) {} };

  const beginPoll = useCallback((feat, slug) => {
    let elapsed = 0;
    pollRef.current = setInterval(async () => {
      elapsed += 2;
      if (elapsed >= 30) setSlowResearch(true);
      try {
        const r = await api.get(`/diaspora/entry/${slug}`);
        if (r.data.status === "ready") {
          clearInterval(pollRef.current); pollRef.current = null;
          setSelected({ ...feat, data: r.data });
          setLoading(false); setSlowResearch(false);
        } else if (r.data.status === "error") {
          clearInterval(pollRef.current); pollRef.current = null;
          setSelected({ ...feat, error: true });
          setLoading(false); setSlowResearch(false);
        }
      } catch {
        clearInterval(pollRef.current); pollRef.current = null;
        setSelected({ ...feat, error: true });
        setLoading(false); setSlowResearch(false);
      }
    }, 2000);
  }, []);

  const runLookup = useCallback(async (feat) => {
    setLoading(true); setSlowResearch(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    stopRotate();
    try {
      const res = await api.post("/diaspora/lookup", {
        country: nameOf(feat), country_code: codeOf(feat), layer: mode,
      });
      if (res.data.status === "ready") {
        setSelected({ ...feat, data: res.data });
        setLoading(false);
      } else {
        beginPoll(feat, res.data.slug);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        setSelected({ ...feat, needAuth: true });
      } else {
        setSelected({ ...feat, error: true });
      }
      setLoading(false);
    }
  }, [mode, beginPoll]);

  const handleClick = useCallback((feat) => {
    setSelected(feat);
    runLookup(feat);
  }, [runLookup]);

  const retry = () => { if (selected) runLookup(selected); };

  const closePanel = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setSelected(null); setLoading(false); setSlowResearch(false);
    try { globeRef.current.controls().autoRotate = true; } catch (e) {}
  };

  const d = selected?.data;

  return (
    <div data-testid="globe-page" className="relative" style={{ background: P.bg, transition: "background 0.6s ease" }}>
      <div className="mx-auto max-w-7xl px-6 pt-14 md:px-12">
        <SectionLabel>The Diaspora Globe</SectionLabel>
        <h1 className="font-serif text-5xl font-semibold text-bone lg:text-6xl">Trace the scattering across the world</h1>

        {/* MODE TOGGLES */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="globe-mode-toggles">
          {MODES.map((m) => {
            const Icon = m.icon;
            const activeMode = mode === m.key;
            return (
              <button
                key={m.key}
                data-testid={`mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className="flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors"
                style={{
                  borderColor: activeMode ? P.accent : "rgba(255,255,255,0.15)",
                  background: activeMode ? P.accent : "transparent",
                  color: activeMode ? "#0A0908" : "#e9e2d0",
                }}
              >
                <Icon className="h-4 w-4" /> {m.label}
              </button>
            );
          })}
        </div>

        <p className="mt-4 max-w-2xl text-lg" style={{ color: P.label }}>
          {INTRO[mode]}
          <span className="mt-1 block text-sm" style={{ color: P.subText }}>
            Brighter countries have curated histories; any country can be explored — the rest are researched on demand.
          </span>
        </p>
      </div>

      <div ref={wrapRef} className="relative mx-auto mt-6 flex max-w-7xl justify-center px-2" data-testid="globe-canvas">
        {countries.length > 0 && (
          <Globe
            ref={globeRef}
            width={dims.w}
            height={dims.h}
            backgroundColor="rgba(0,0,0,0)"
            showGlobe={true}
            showGraticules={true}
            showAtmosphere={true}
            atmosphereColor={P.atmosphere}
            atmosphereAltitude={0.22}
            polygonsData={countries}
            polygonCapColor={capColor}
            polygonSideColor={() => P.side}
            polygonStrokeColor={() => P.stroke}
            polygonAltitude={(feat) => (feat === hoverD || (selected && codeOf(feat) === codeOf(selected)) ? 0.06 : 0.012)}
            onPolygonHover={setHoverD}
            onPolygonClick={handleClick}
            polygonLabel={(feat) => `<div style="background:${P.panelBg};border:1px solid ${P.stroke};color:${P.hover};padding:4px 10px;font-family:monospace;font-size:11px">${nameOf(feat)}</div>`}
            polygonsTransitionDuration={300}
            arcsData={arcs}
            arcColor={() => [P.accent, P.hover]}
            arcStroke={0.5}
            arcAltitude={0.25}
            arcDashLength={0.4}
            arcDashGap={0.15}
            arcDashAnimateTime={2200}
          />
        )}

        {/* MINI LEGEND */}
        <div
          className="pointer-events-none absolute left-4 top-4 z-20 border p-3 backdrop-blur-sm"
          style={{ borderColor: P.border, background: `${P.panelBg}cc` }}
          data-testid="globe-legend"
        >
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: P.accent }}>Legend</p>
          <LegendRow color={P.active} label={LEGEND[mode].active} />
          <LegendRow color={P.selected} label={LEGEND[mode].selected} />
          <LegendRow color={P.land} label={LEGEND[mode].land} />
        </div>

        {/* DID YOU KNOW TICKER */}
        <div
          className="absolute bottom-4 left-1/2 z-20 w-[92%] max-w-2xl -translate-x-1/2 border px-4 py-3 backdrop-blur-sm"
          style={{ borderColor: P.border, background: `${P.panelBg}d9` }}
          data-testid="globe-ticker"
        >
          <div className="flex items-start gap-3">
            <Lightbulb className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: P.accent }} />
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: P.accent }}>Did you know?</p>
              <AnimatePresence mode="wait">
                <motion.p
                  key={`${mode}-${factIdx}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.4 }}
                  className="mt-1 text-sm leading-snug"
                  style={{ color: P.subText }}
                  data-testid="globe-fact"
                >
                  {(FACTS[mode] || [])[factIdx]}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* SIDE PANEL */}
      <AnimatePresence>
        {selected && (
          <motion.aside
            key="panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[70] h-full w-full max-w-md overflow-y-auto border-l shadow-2xl"
            style={{ background: P.panelBg, borderColor: P.border }}
            data-testid="diaspora-panel"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b px-6 py-4 backdrop-blur"
              style={{ borderColor: P.border, background: `${P.panelBg}f2` }}>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" style={{ color: P.accent }} />
                <h2 className="font-serif text-2xl text-bone" data-testid="panel-country">{nameOf(selected)}</h2>
              </div>
              <button onClick={closePanel} data-testid="panel-close" className="text-bone/60 hover:text-bone">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-6">
              {loading && (
                <div className="flex flex-col items-center gap-3 py-16 text-center">
                  <div className="h-9 w-9 animate-spin rounded-full border-2" style={{ borderColor: P.border, borderTopColor: P.accent }} />
                  <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: P.accent }}>
                    {mode === "colonizer" ? "Tracing the exploitation…" : mode === "resistance" ? "Gathering the resistance…" : "Researching the diaspora…"}
                  </p>
                  {slowResearch && <p className="max-w-xs text-sm" style={{ color: P.subText }}>Still researching — this is a first-time deep lookup and can take up to a minute. Hang tight.</p>}
                </div>
              )}

              {!loading && selected?.needAuth && (
                <div className="py-16 text-center" data-testid="panel-signin">
                  <p className="mx-auto max-w-xs" style={{ color: P.subText }}>
                    This country hasn't been researched yet. Sign in to unlock on-demand AI research — curated histories stay free to explore.
                  </p>
                  <button onClick={login} data-testid="panel-signin-btn" className="mt-4 border px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-bone" style={{ borderColor: P.accent }}>Sign in to research</button>
                </div>
              )}

              {!loading && selected?.error && (
                <div className="py-16 text-center">
                  <p style={{ color: P.subText }}>Couldn't load this country's history.</p>
                  <button onClick={retry} data-testid="panel-retry" className="mt-4 border px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-bone" style={{ borderColor: P.accent }}>Try again</button>
                </div>
              )}

              {!loading && d && (
                <div className="mb-6">
                  <AssignControl P={P} mode={mode} selected={selected} d={d} user={user} learners={learners} />
                </div>
              )}

              {!loading && d && mode === "diaspora" && <DiasporaBody d={d} P={P} />}
              {!loading && d && mode === "colonizer" && <ColonizerBody d={d} P={P} />}
              {!loading && d && mode === "resistance" && <ResistanceBody d={d} P={P} />}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <div className="h-16" />
    </div>
  );
}

/* ── LAYER PANELS ─────────────────────────────────────────────── */

function DiasporaBody({ d, P }) {
  return (
    <div className="space-y-8">
      <Tags P={P} items={[d.region, d.arrival_period]} ai={d.ai_generated} />
      <Block P={P} icon={Clock} title="When Africans Arrived">{d.arrival}</Block>
      <div>
        <PanelHead P={P} icon={Users}>Origins & Tribes</PanelHead>
        {d.origins && <p className="mb-3 text-sm leading-relaxed" style={{ color: P.subText }}>Taken from {d.origins}</p>}
        <Chips P={P} items={d.tribes} testid="tribe" />
      </div>
      <div>
        <PanelHead P={P} icon={Sparkles}>Notable Contributors</PanelHead>
        <div className="space-y-3">
          {(d.contributions || []).map((c, i) => (
            <div key={i} className="border-l-2 p-4" style={{ borderColor: P.active, background: "rgba(255,255,255,0.03)" }} data-testid={`contributor-${i}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-serif text-lg text-bone">{c.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: P.accent }}>{c.field}</span>
              </div>
              <p className="mt-1 text-sm leading-relaxed" style={{ color: P.subText }}>{c.contribution}</p>
            </div>
          ))}
        </div>
      </div>
      {(d.cultural_contributions || []).length > 0 && (
        <div>
          <PanelHead P={P} icon={Music}>Cultural Contributions</PanelHead>
          <Bullets P={P} items={d.cultural_contributions} />
        </div>
      )}
      <Block P={P} icon={ShieldAlert} title="Oppression Faced">{d.oppression}</Block>
      <Block P={P} icon={Landmark} title="Legacy">{d.legacy}</Block>
    </div>
  );
}

function ColonizerBody({ d, P }) {
  return (
    <div className="space-y-8">
      <Tags P={P} items={[d.region, d.trade, d.arrival_period]} ai={d.ai_generated} />
      <Block P={P} icon={Coins} title="Role in the Slave Trade & Colonization">{d.summary}</Block>
      <Block P={P} icon={Sparkles} title="How They Profited">{d.profit}</Block>
      <div>
        <PanelHead P={P} icon={MapPin}>African Nations Colonized / Enslaved</PanelHead>
        <div className="space-y-3">
          {(d.colonized || []).map((c, i) => (
            <div key={i} className="border-l-2 p-4" style={{ borderColor: P.active, background: "rgba(255,255,255,0.03)" }} data-testid={`colonized-${i}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-serif text-lg text-bone">{c.country}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: P.accent }}>{c.period}</span>
              </div>
              {c.note && <p className="mt-1 text-sm leading-relaxed" style={{ color: P.subText }}>{c.note}</p>}
            </div>
          ))}
        </div>
      </div>
      <Block P={P} icon={Ban} title="Abolition / End">{d.abolition}</Block>
      <Block P={P} icon={Landmark} title="Legacy">{d.legacy}</Block>
    </div>
  );
}

function ResistanceBody({ d, P }) {
  return (
    <div className="space-y-8">
      <Tags P={P} items={[d.region, d.adversary ? `vs ${d.adversary}` : "", d.arrival_period]} ai={d.ai_generated} />
      <Block P={P} icon={Flag} title="The Fight for Freedom">{d.summary}</Block>
      <div>
        <PanelHead P={P} icon={Swords}>Battles of Resistance</PanelHead>
        <div className="space-y-4">
          {(d.battles || []).map((b, i) => (
            <div key={i} className="border-l-2 p-4" style={{ borderColor: P.active, background: "rgba(255,255,255,0.03)" }} data-testid={`battle-${i}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-serif text-lg text-bone">{b.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: P.accent }}>{b.year}</span>
              </div>
              {(b.leaders || b.adversary) && (
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: P.label }}>
                  {b.leaders}{b.leaders && b.adversary ? " · " : ""}{b.adversary ? `against ${b.adversary}` : ""}
                </p>
              )}
              {b.description && <p className="mt-2 text-sm leading-relaxed" style={{ color: P.subText }}>{b.description}</p>}
              {b.outcome && <p className="mt-2 text-sm leading-relaxed text-bone"><span className="font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: P.accent }}>Outcome — </span>{b.outcome}</p>}
            </div>
          ))}
        </div>
      </div>
      <Block P={P} icon={Landmark} title="Legacy">{d.legacy}</Block>
    </div>
  );
}

/* ── SHARED PRIMITIVES ────────────────────────────────────────── */

function Tags({ P, items, ai }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.filter(Boolean).map((t, i) => (
        <span key={i} className="border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em]" style={{ borderColor: P.border, color: P.label }}>{t}</span>
      ))}
      {ai && <span className="border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em]" style={{ borderColor: P.accent, background: "rgba(255,255,255,0.06)", color: P.hover }}>AI Researched</span>}
    </div>
  );
}

function Chips({ P, items, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(items || []).map((t, i) => (
        <span key={i} className="border px-3 py-1 text-sm" style={{ borderColor: P.border, background: "rgba(255,255,255,0.03)", color: P.hover }} data-testid={`${testid}-${i}`}>{t}</span>
      ))}
    </div>
  );
}

function Bullets({ P, items }) {
  return (
    <ul className="space-y-2">
      {(items || []).map((c, i) => (
        <li key={i} className="flex gap-2 text-sm leading-relaxed" style={{ color: P.subText }}><span style={{ color: P.accent }}>▸</span>{c}</li>
      ))}
    </ul>
  );
}

function PanelHead({ P, icon: Icon, children }) {
  return (
    <h3 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: P.accent }}>
      <Icon className="h-4 w-4" /> {children}
    </h3>
  );
}

function Block({ P, icon, title, children }) {
  if (!children) return null;
  return (
    <div>
      <PanelHead P={P} icon={icon}>{title}</PanelHead>
      <p className="text-sm leading-relaxed" style={{ color: P.subText }}>{children}</p>
    </div>
  );
}


function LegendRow({ color, label }) {
  return (
    <div className="mb-1.5 flex items-center gap-2 last:mb-0">
      <span className="h-3 w-3 flex-shrink-0 rounded-sm" style={{ background: color }} />
      <span className="text-[11px] text-bone/80">{label}</span>
    </div>
  );
}

const LAYER_LABEL = { diaspora: "Diaspora", colonizer: "Colonizers & Slave Trade", resistance: "Resistance" };

function AssignControl({ P, mode, selected, d, user, learners }) {
  const [open, setOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const country = nameOf(selected);
  const title = `${country} — ${LAYER_LABEL[mode]}`;

  const assign = async (learner) => {
    setAssigning(true);
    try {
      await api.post("/assignments", {
        learner_id: learner.id,
        item_type: "globe",
        layer: mode,
        country,
        country_code: codeOf(selected),
        slug: d.slug || "",
        title,
      });
      toast.success(`Assigned "${country}" to ${learner.name}`);
      setOpen(false);
    } catch {
      toast.error("Could not assign. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  if (!user) {
    return (
      <button
        onClick={() => toast.info("Sign in to assign explorations to your learners.")}
        data-testid="assign-signin-hint"
        className="flex w-full items-center justify-center gap-2 border py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-bone/70"
        style={{ borderColor: P.border }}
      >
        <GraduationCap className="h-4 w-4" /> Sign in to assign to a learner
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="assign-learner-btn"
        className="flex w-full items-center justify-center gap-2 border py-2.5 font-mono text-[11px] uppercase tracking-[0.15em]"
        style={{ borderColor: P.accent, color: P.accent }}
      >
        <GraduationCap className="h-4 w-4" /> Assign to a learner
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-2 border p-2" style={{ borderColor: P.border, background: "rgba(255,255,255,0.03)" }} data-testid="assign-learner-list">
          {learners.length === 0 ? (
            <p className="px-2 py-3 text-center text-sm" style={{ color: P.subText }}>
              No learners yet. Add one from your Dashboard.
            </p>
          ) : (
            learners.map((l) => (
              <button
                key={l.id}
                disabled={assigning}
                onClick={() => assign(l)}
                data-testid={`assign-to-${l.id}`}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-bone hover:bg-white/5 disabled:opacity-50"
              >
                <span>{l.name}{l.level ? <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: P.subText }}>{l.level}</span> : null}</span>
                <Check className="h-4 w-4" style={{ color: P.accent }} />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
